import { useState, useRef, useEffect, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Trophy, RefreshCw, Send, Loader2, Target, Zap, Activity,
  Heart, CheckSquare, TrendingUp, TrendingDown, Minus, ChevronRight,
  BarChart2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from "recharts";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type Period = "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
type RangeType = "rolling" | "calendar";
type View = "scorecard" | "trend";

interface ScorecardData {
  period: Period;
  rangeType: RangeType;
  label: string;
  daysInRange: number;
  scores: { goals: number; habits: number; trackers: number; health: number; tasks: number };
  composite: number;
  strengths: string[];
  improvements: string[];
  coachMessage: string;
  priorityActions: string[];
  metrics: {
    goals: { active: number; completed: number; overdue: number };
    tasks: { completed: number; pending: number; overdue: number; urgentTotal: number; urgentCompleted: number };
    habits: { active: number; completionRate: number; entriesLogged: number };
    trackers: { count: number; entriesLogged: number; daysLogged: number };
    health: { workouts: number; waterMl: number; mealDaysLogged: number; metricsTracked: number };
    calendar: { events: number };
  };
}

interface TrendPoint {
  label: string;
  startDate: string;
  endDate: string;
  composite: number;
  scores: { goals: number; habits: number; tasks: number; health: number; trackers: number };
  daysInPeriod: number;
}

interface ChatMessage { role: "user" | "assistant"; content: string }

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const TREND_WINDOWS: Record<Period, { count: number; label: string }[]> = {
  daily:     [{ count: 5, label: "5 Days" }, { count: 14, label: "14 Days" }, { count: 21, label: "21 Days" }, { count: 30, label: "30 Days" }],
  weekly:    [{ count: 1, label: "1 Week" }, { count: 2, label: "2 Weeks" }, { count: 3, label: "3 Weeks" }, { count: 4, label: "4 Weeks" }],
  monthly:   [{ count: 1, label: "1 Month" }, { count: 2, label: "2 Months" }, { count: 3, label: "3 Months" }],
  quarterly: [{ count: 2, label: "2 Quarters" }, { count: 3, label: "3 Quarters" }, { count: 4, label: "4 Quarters" }],
  yearly:    [{ count: 1, label: "1 Year" }, { count: 2, label: "2 Years" }, { count: 3, label: "3 Years" }],
};

const AREA_LINES: { key: string; label: string; color: string }[] = [
  { key: "composite", label: "Composite", color: "hsl(var(--primary))" },
  { key: "goals",     label: "Goals",     color: "#10b981" },
  { key: "habits",    label: "Habits",    color: "#f59e0b" },
  { key: "tasks",     label: "Tasks",     color: "#6366f1" },
  { key: "health",    label: "Health",    color: "#ef4444" },
  { key: "trackers",  label: "Trackers",  color: "#8b5cf6" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────────────────────

function scoreColor(s: number) {
  if (s >= 8) return "text-emerald-500";
  if (s >= 6) return "text-amber-500";
  if (s >= 4) return "text-orange-500";
  return "text-red-500";
}
function scoreBg(s: number) {
  if (s >= 8) return "bg-emerald-500/10 border-emerald-500/30";
  if (s >= 6) return "bg-amber-500/10 border-amber-500/30";
  if (s >= 4) return "bg-orange-500/10 border-orange-500/30";
  return "bg-red-500/10 border-red-500/30";
}
function scoreTrend(s: number) {
  if (s >= 7) return <TrendingUp className="w-3 h-3 text-emerald-500" />;
  if (s >= 5) return <Minus className="w-3 h-3 text-amber-500" />;
  return <TrendingDown className="w-3 h-3 text-red-500" />;
}

// ─────────────────────────────────────────────────────────────────────────────
// ScoreCard mini card
// ─────────────────────────────────────────────────────────────────────────────

function ScoreCard({ label, score, icon, subtitle }: { label: string; score: number; icon: React.ReactNode; subtitle?: string }) {
  return (
    <div className={cn("flex flex-col items-center gap-1 p-3 rounded-xl border", scoreBg(score))}>
      <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className={cn("text-3xl font-black tabular-nums", scoreColor(score))}>
        {score}<span className="text-base font-medium text-muted-foreground">/10</span>
      </div>
      <div className="flex items-center gap-1">
        {scoreTrend(score)}
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Trend chart panel
// ─────────────────────────────────────────────────────────────────────────────

export function TrendPanel({ period, onClose }: { period: Period; onClose: () => void }) {
  const windows = TREND_WINDOWS[period];
  const [windowCount, setWindowCount] = useState(windows[windows.length - 1].count);
  const [activeLines, setActiveLines] = useState<Set<string>>(
    new Set(["composite", "goals", "habits", "tasks", "health", "trackers"])
  );

  // Drives an immediate re-render when Retry is clicked so the error panel
  // disappears synchronously, before React Query's async pending-state
  // notification arrives. Using useState (not useRef) is required: only state
  // changes schedule a re-render that removes the error element from the DOM.
  //
  // Cleared in onSettled (not via a useEffect watching isPending): TanStack
  // Query batches observer notifications asynchronously, so a fast rejection
  // can advance the snapshot from pending → error before React ever renders
  // the pending state, leaving a useEffect-based guard permanently stuck true.
  // onSettled fires at mutation settlement regardless of the React render cycle.
  const [retryInFlight, setRetryInFlight] = useState(false);

  const trendMutation = useMutation({
    mutationFn: async (count: number) => {
      const res = await apiRequest("POST", "/api/scorecard/trend", { period, windowCount: count });
      return res.json() as Promise<TrendPoint[]>;
    },
    onSettled: () => {
      setRetryInFlight(false);
    },
  });

  // Fetch when window changes
  useEffect(() => {
    trendMutation.mutate(windowCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowCount, period]);

  const toggleLine = useCallback((key: string) => {
    setActiveLines(prev => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  }, []);

  // Flatten trend points for recharts
  const chartData = (trendMutation.data ?? []).map(pt => ({
    label: pt.label,
    composite: pt.composite,
    goals: pt.scores.goals,
    habits: pt.scores.habits,
    tasks: pt.scores.tasks,
    health: pt.scores.health,
    trackers: pt.scores.trackers,
  }));

  const changeDir = (() => {
    if (chartData.length < 2) return null;
    const diff = chartData[chartData.length - 1].composite - chartData[0].composite;
    if (diff > 0.3) return { icon: <TrendingUp className="w-4 h-4 text-emerald-500" />, text: `+${diff.toFixed(1)} over window`, color: "text-emerald-500" };
    if (diff < -0.3) return { icon: <TrendingDown className="w-4 h-4 text-red-500" />, text: `${diff.toFixed(1)} over window`, color: "text-red-500" };
    return { icon: <Minus className="w-4 h-4 text-amber-500" />, text: "Flat", color: "text-amber-500" };
  })();

  return (
    <div className="space-y-4">
      {/* Window buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Show:</span>
        {windows.map(w => (
          <Button
            key={w.count}
            variant={windowCount === w.count ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => setWindowCount(w.count)}
          >
            {w.label}
          </Button>
        ))}
      </div>

      {/* Loading */}
      {trendMutation.isPending && (
        <div className="flex items-center justify-center py-16 gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading trend data…</span>
        </div>
      )}

      {/* Error — hidden while a retry is already in-flight.
           retryInFlight is useState so setting it true in the click handler
           immediately schedules a re-render that removes the error panel from
           the DOM, before React Query's async pending-state notification fires. */}
      {trendMutation.isError && !trendMutation.isPending && !retryInFlight && (
        <div className="flex flex-col items-center gap-3 py-8">
          <p className="text-sm text-destructive">Failed to load trend.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRetryInFlight(true);
              trendMutation.mutate(windowCount);
            }}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
          </Button>
        </div>
      )}

      {/* Empty state — server returned zero buckets */}
      {trendMutation.isSuccess && chartData.length === 0 && !trendMutation.isPending && (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <p className="text-sm text-muted-foreground">No trend data available for this period.</p>
          <p className="text-xs text-muted-foreground">Try selecting a wider window above.</p>
        </div>
      )}

      {/* Chart */}
      {trendMutation.isSuccess && chartData.length > 0 && !trendMutation.isPending && (
        <>
          {/* Summary pill */}
          {changeDir && (
            <div className="flex items-center gap-2">
              {changeDir.icon}
              <span className={cn("text-sm font-medium", changeDir.color)}>{changeDir.text}</span>
              <span className="text-xs text-muted-foreground">
                · avg {(chartData.reduce((s, d) => s + d.composite, 0) / chartData.length).toFixed(1)}/10
              </span>
            </div>
          )}

          {/* Area line toggles */}
          <div className="flex flex-wrap gap-1.5">
            {AREA_LINES.map(line => (
              <button
                key={line.key}
                onClick={() => toggleLine(line.key)}
                className={cn(
                  "flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium transition-all",
                  activeLines.has(line.key)
                    ? "opacity-100"
                    : "opacity-30"
                )}
                style={{ borderColor: line.color, color: line.color }}
              >
                <span
                  className="inline-block h-1.5 rounded-full"
                  style={{ width: line.key === "composite" ? 16 : 10, backgroundColor: line.color }}
                />
                {line.label}
              </button>
            ))}
          </div>

          {/* The chart */}
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={false}
                interval={chartData.length <= 7 ? 0 : "preserveStartEnd"}
              />
              <YAxis
                domain={[0, 10]}
                ticks={[0, 2, 4, 6, 8, 10]}
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, padding: "6px 10px", borderRadius: 8 }}
                labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                formatter={(v: number, name: string) => [
                  `${v.toFixed(1)}/10`,
                  AREA_LINES.find(l => l.key === name)?.label ?? name,
                ]}
              />
              {AREA_LINES.map(line => (
                activeLines.has(line.key) && (
                  <Line
                    key={line.key}
                    type="monotone"
                    dataKey={line.key}
                    stroke={line.color}
                    strokeWidth={line.key === "composite" ? 3 : 1.5}
                    strokeDasharray={line.key === "composite" ? undefined : "4 3"}
                    dot={{ r: line.key === "composite" ? 3 : 2, fill: line.color }}
                    activeDot={{ r: 5 }}
                  />
                )
              ))}
            </LineChart>
          </ResponsiveContainer>

          {/* No-data edge — single bucket */}
          {chartData.length === 1 && (
            <div className="flex flex-col items-center gap-1">
              <p className="text-xs text-muted-foreground text-center">
                Only one data point — use a wider window to see a trend.
              </p>
              <p className="text-xs text-muted-foreground text-center">
                Try selecting a wider window above.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main dialog
// ─────────────────────────────────────────────────────────────────────────────

interface ScorecardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ScorecardDialog({ open, onOpenChange }: ScorecardDialogProps) {
  const [period, setPeriod] = useState<Period>("daily");
  const [rangeType, setRangeType] = useState<RangeType>("rolling");
  const [view, setView] = useState<View>("scorecard");
  const [scorecard, setScorecard] = useState<ScorecardData | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const chatBottomRef = useRef<HTMLDivElement>(null);

  const analyzeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/scorecard/analyze", { period, rangeType });
      return res.json() as Promise<ScorecardData>;
    },
    onSuccess: (data) => { setScorecard(data); setChatMessages([]); },
  });

  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      const res = await apiRequest("POST", "/api/scorecard/chat", {
        message,
        conversationHistory: chatMessages,
        scorecardContext: scorecard ? {
          label: scorecard.label,
          scores: scorecard.scores,
          composite: scorecard.composite,
          strengths: scorecard.strengths,
          improvements: scorecard.improvements,
          coachMessage: scorecard.coachMessage,
        } : undefined,
      });
      return res.json() as Promise<{ response: string }>;
    },
    onSuccess: (data, message) => {
      setChatMessages(prev => [
        ...prev,
        { role: "user", content: message },
        { role: "assistant", content: data.response },
      ]);
      setChatInput("");
    },
  });

  // Auto-generate scorecard when dialog opens or period/rangeType changes
  useEffect(() => {
    if (open && view === "scorecard") {
      setScorecard(null);
      setChatMessages([]);
      analyzeMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, period, rangeType]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const handlePeriodChange = (p: string) => {
    setPeriod(p as Period);
    setScorecard(null);
    // stay in whatever view the user is in; trend will re-fetch with new period
  };

  const handleSendChat = () => {
    const msg = chatInput.trim();
    if (!msg || chatMutation.isPending) return;
    chatMutation.mutate(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendChat(); }
  };

  const needsRangeToggle = period === "monthly" || period === "quarterly";

  const periodDays: Record<Period, string> = {
    daily:     "today",
    weekly:    "last 7 days",
    monthly:   rangeType === "calendar" ? "this month" : "last 30 days",
    quarterly: rangeType === "calendar" ? "this quarter" : "last 90 days",
    yearly:    "last 365 days",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold">Performance Scorecard</DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                AI-powered assessment of {periodDays[period]}
              </p>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-hidden">
          <div className="px-6 py-4 space-y-4">

            {/* Period tabs — 5 options */}
            <Tabs value={period} onValueChange={handlePeriodChange}>
              <TabsList className="w-full grid grid-cols-5">
                <TabsTrigger value="daily">Daily</TabsTrigger>
                <TabsTrigger value="weekly">Weekly</TabsTrigger>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="quarterly">Quarterly</TabsTrigger>
                <TabsTrigger value="yearly">Yearly</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Rolling / Calendar toggle (monthly & quarterly only) */}
            {needsRangeToggle && (
              <div className="flex gap-2 justify-center">
                <Button
                  variant={rangeType === "rolling" ? "default" : "outline"}
                  size="sm" className="text-xs h-7"
                  onClick={() => setRangeType("rolling")}
                >
                  {period === "monthly" ? "Last 30 days" : "Last 90 days"}
                </Button>
                <Button
                  variant={rangeType === "calendar" ? "default" : "outline"}
                  size="sm" className="text-xs h-7"
                  onClick={() => setRangeType("calendar")}
                >
                  {period === "monthly" ? "This month" : "This quarter"}
                </Button>
              </div>
            )}

            {/* Scorecard / Trend view toggle */}
            <div className="flex items-center gap-2 border rounded-lg p-1 w-fit">
              <Button
                variant={view === "scorecard" ? "default" : "ghost"}
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => {
                  setView("scorecard");
                  if (!scorecard && !analyzeMutation.isPending) analyzeMutation.mutate();
                }}
              >
                <Trophy className="w-3.5 h-3.5" />
                Scorecard
              </Button>
              <Button
                variant={view === "trend" ? "default" : "ghost"}
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => setView("trend")}
              >
                <BarChart2 className="w-3.5 h-3.5" />
                Trend
              </Button>
            </div>

            {/* ── TREND VIEW ────────────────────────────────────────────── */}
            {view === "trend" && (
              <TrendPanel period={period} onClose={() => setView("scorecard")} />
            )}

            {/* ── SCORECARD VIEW ────────────────────────────────────────── */}
            {view === "scorecard" && (
              <>
                {/* Loading */}
                {analyzeMutation.isPending && (
                  <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-sm">Analyzing your performance…</p>
                      <p className="text-xs text-muted-foreground mt-1">Your coach is reviewing the data</p>
                    </div>
                  </div>
                )}

                {/* Error */}
                {analyzeMutation.isError && !analyzeMutation.isPending && (
                  <div className="flex flex-col items-center gap-3 py-8">
                    <p className="text-sm text-destructive">Failed to generate scorecard.</p>
                    <Button variant="outline" size="sm" onClick={() => analyzeMutation.mutate()}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Try again
                    </Button>
                  </div>
                )}

                {/* Results */}
                {scorecard && !analyzeMutation.isPending && (
                  <div className="space-y-5">
                    {/* Composite */}
                    <div className={cn("rounded-2xl border p-4 flex items-center gap-4", scoreBg(Math.round(scorecard.composite)))}>
                      <div className="flex-1">
                        <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-1">Composite Score</p>
                        <div className="flex items-baseline gap-1">
                          <span className={cn("text-5xl font-black tabular-nums", scoreColor(Math.round(scorecard.composite)))}>
                            {scorecard.composite.toFixed(1)}
                          </span>
                          <span className="text-lg text-muted-foreground font-medium">/10</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {scorecard.label} · {scorecard.daysInRange} day{scorecard.daysInRange === 1 ? "" : "s"}
                        </p>
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => { setScorecard(null); analyzeMutation.mutate(); }}
                        className="shrink-0 text-muted-foreground"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Area scores */}
                    <div className="grid grid-cols-5 gap-2">
                      <ScoreCard label="Goals"    score={scorecard.scores.goals}    icon={<Target className="w-3.5 h-3.5" />}      subtitle={`${scorecard.metrics.goals.active} active`} />
                      <ScoreCard label="Habits"   score={scorecard.scores.habits}   icon={<Zap className="w-3.5 h-3.5" />}         subtitle={`${scorecard.metrics.habits.completionRate}%`} />
                      <ScoreCard label="Tasks"    score={scorecard.scores.tasks}    icon={<CheckSquare className="w-3.5 h-3.5" />}  subtitle={`${scorecard.metrics.tasks.overdue} overdue`} />
                      <ScoreCard label="Health"   score={scorecard.scores.health}   icon={<Heart className="w-3.5 h-3.5" />}       subtitle={`${scorecard.metrics.health.workouts} workouts`} />
                      <ScoreCard label="Trackers" score={scorecard.scores.trackers} icon={<Activity className="w-3.5 h-3.5" />}    subtitle={`${scorecard.metrics.trackers.daysLogged}d logged`} />
                    </div>

                    {/* Coach message */}
                    <div className="rounded-xl bg-muted/50 border p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                          <Trophy className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">Coach</span>
                      </div>
                      <p className="text-sm leading-relaxed">{scorecard.coachMessage}</p>
                    </div>

                    {/* Strengths + Improvements */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                          <TrendingUp className="w-3.5 h-3.5" /> What's working
                        </p>
                        <ul className="space-y-1.5">
                          {scorecard.strengths.map((s, i) => (
                            <li key={i} className="text-xs text-muted-foreground flex gap-2">
                              <span className="text-emerald-500 shrink-0 mt-0.5">✓</span>
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-red-500 flex items-center gap-1.5">
                          <TrendingDown className="w-3.5 h-3.5" /> Fix these now
                        </p>
                        <ul className="space-y-1.5">
                          {scorecard.improvements.map((s, i) => (
                            <li key={i} className="text-xs text-muted-foreground flex gap-2">
                              <span className="text-red-500 shrink-0 mt-0.5">▲</span>
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Priority actions */}
                    {scorecard.priorityActions.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Priority actions</p>
                        <div className="space-y-1.5">
                          {scorecard.priorityActions.map((action, i) => (
                            <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-muted/40 border">
                              <Badge variant="outline" className="shrink-0 text-xs px-1.5 py-0 h-5 font-bold">{i + 1}</Badge>
                              <span className="text-xs">{action}</span>
                              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 ml-auto mt-0.5" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Chat */}
                    <div className="border-t pt-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Ask your coach</p>
                      {chatMessages.length > 0 && (
                        <div className="space-y-3 mb-3 max-h-64 overflow-y-auto">
                          {chatMessages.map((msg, i) => (
                            <div key={i} className={cn("rounded-xl p-3 text-sm",
                              msg.role === "user"
                                ? "bg-primary text-primary-foreground ml-8"
                                : "bg-muted/60 border mr-8"
                            )}>{msg.content}</div>
                          ))}
                          {chatMutation.isPending && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Coach is thinking…
                            </div>
                          )}
                          <div ref={chatBottomRef} />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Textarea
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Ask about your scores, request a plan, or get specific advice…"
                          className="min-h-[60px] max-h-[120px] text-sm resize-none"
                          disabled={chatMutation.isPending}
                        />
                        <Button
                          size="icon"
                          onClick={handleSendChat}
                          disabled={!chatInput.trim() || chatMutation.isPending}
                          className="h-auto shrink-0"
                        >
                          {chatMutation.isPending
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">Press Enter to send · Shift+Enter for newline</p>
                    </div>
                  </div>
                )}
              </>
            )}

          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
